import { ApolloLink } from '@apollo/client';
import type { TypePolicies } from '@apollo/client/cache';
import { getOperationDefinition } from '@apollo/client/utilities/internal';
import type { DocumentNode } from 'graphql';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import isString from 'lodash/isString';
import { map } from 'rxjs';

import type { Literal } from '../services/guards';
import { isLiteral } from '../services/guards';
import type { Nullable } from '../services/object';

import type { ResponseTransform } from './scalars';
import { responseTransform } from './scalars';

type FieldTransforms = ReadonlyMap<string, ReadonlyMap<string, ResponseTransform>>;

const resolveTransform = (fieldPolicy: unknown): Nullable<ResponseTransform> => (
  isLiteral(fieldPolicy)
    ? (fieldPolicy as Partial<Record<typeof responseTransform, ResponseTransform>>)[responseTransform] ?? null
    : null
);

export const buildFieldTransforms = (typePolicies: TypePolicies): FieldTransforms => {
  const result = new Map<string, ReadonlyMap<string, ResponseTransform>>();

  Object.entries(typePolicies).forEach(([typeName, typePolicy]) => {
    const fieldEntries = Object.entries(typePolicy.fields ?? {}).flatMap(
      ([fieldName, fieldPolicy]): [string, ResponseTransform][] => {
        const transform = resolveTransform(fieldPolicy);
        return isNil(transform) ? [] : [[fieldName, transform]];
      },
    );

    if (fieldEntries.length > 0) {
      result.set(typeName, new Map(fieldEntries));
    }
  });

  return result;
};

const rootTypenames = {
  query: 'Query',
  mutation: 'Mutation',
  subscription: 'Subscription',
} as const;

const rootTypename = (
  document: DocumentNode,
): string => rootTypenames[getOperationDefinition(document)?.operation ?? 'query'];

export const transformData = (
  value: unknown,
  transforms: FieldTransforms,
  typenameFallback?: string,
): unknown => {
  if (isArray(value)) {
    let changed = false;
    const next = value.map((element) => {
      const nextElement = transformData(element, transforms);
      changed = changed || nextElement !== element;
      return nextElement;
    });

    return changed ? next : value;
  }

  if (!isLiteral(value)) {
    return value;
  }

  const typename = isString(value.__typename) ? value.__typename : typenameFallback;
  const fieldTransforms = isNil(typename) ? undefined : transforms.get(typename);

  let next: Nullable<Literal> = null;

  Object.keys(value).forEach((field) => {
    const fieldValue = value[field];
    const transform = fieldTransforms?.get(field);
    const nextValue = isNil(transform)
      ? transformData(fieldValue, transforms)
      : transform(fieldValue);

    if (nextValue !== fieldValue) {
      next = next ?? { ...value };
      next[field] = nextValue;
    }
  });

  return next ?? value;
};

// Converts minor-unit scalars (Cents, BasisPoint, VatTypeInput) in every response
// — queries, mutations and no-cache fetches alike — so the whole app, including
// the cache, only ever sees display units. Cache field policies can't do this
// consistently: their `read` fns never run for mutation results or no-cache data.
export const createScalarTransformLink = (typePolicies: TypePolicies): ApolloLink => {
  const transforms = buildFieldTransforms(typePolicies);

  return new ApolloLink((operation, forward) => forward(operation).pipe(map((result) => {
    if (isNil(result.data)) {
      return result;
    }

    const data = transformData(result.data, transforms, rootTypename(operation.query));

    return data === result.data ? result : { ...result, data: data as typeof result.data };
  })));
};
